import { useSetAtom } from 'jotai/index';
import { useEffect, useState } from 'react';

import { CaregiverCallConfirmationType } from '@/shared/call/CaregiverCallConfirmationType';
import { usePhoneCallContext } from '@/shared/contexts/phoneCallHandlerContext/PhoneCallContext';

import { CallStatus } from '../model/CallStatus';
import {
    callStatusAtom,
    hangUpPhoneCallAtom,
    isConnectingAtom,
    makeCallAtom,
    phoneCallHandlerAtom,
    shutdownConfirmationAtom,
} from '../model/store';
import { PhoneCallHandler, PhoneCallStatus } from './PhoneCallHandler';
import { useCaregiverCallConfirmationBase } from './useCaregiverCallConfirmationBase';

type UseCaregiverPhoneCallConfirmationProps = {
    consultationUid: string;
    onClose: () => void;
};

export const useCaregiverPhoneCallConfirmation = ({
    consultationUid,
    onClose,
}: UseCaregiverPhoneCallConfirmationProps) => {
    const [phoneCallStatus, setPhoneCallStatus] =
        useState<PhoneCallStatus | null>(null);
    const { setPhoneCallHandler: setPhoneCallHandlerContext } =
        usePhoneCallContext();
    const setPhoneCallHandler = useSetAtom(phoneCallHandlerAtom);
    const setIsConnecting = useSetAtom(isConnectingAtom);
    const setCallStatus = useSetAtom(callStatusAtom);
    const shutdownConfirmation = useSetAtom(shutdownConfirmationAtom);
    const hangUpPhoneCall = useSetAtom(hangUpPhoneCallAtom);
    const makeCall = useSetAtom(makeCallAtom);

    const { confirmationData, ...baseResult } =
        useCaregiverCallConfirmationBase({
            confirmationType: CaregiverCallConfirmationType.phone,
            consultationUid,
            onClose,
        });

    useEffect(() => {
        if (!confirmationData) return;

        const phoneCallHandlerNew = new PhoneCallHandler(confirmationData, {
            onHangUp: () => {
                setPhoneCallStatus('Disconnected');
                setIsConnecting(false);
                setCallStatus(CallStatus.notStarted);
            },
        });

        phoneCallHandlerNew.onStatusChanged((status) => {
            setPhoneCallStatus(status);
        });

        setPhoneCallHandler(phoneCallHandlerNew);
        setPhoneCallHandlerContext(phoneCallHandlerNew);

        return () => {
            shutdownConfirmation();
        };
    }, [confirmationData]);

    const startCall = () => {
        makeCall();
    };

    return {
        ...baseResult,
        phoneCallStatus,
        startCall,
        hangUpPhoneCall,
    };
};
